import { create } from 'zustand';
import { notifyDataChanged } from '../utils/dataEvents';
import { getStages } from '../services/api';

interface OpportunityStage {
  id: string;
  name: string;
  orderIndex: number;
  probability: number;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    opportunities: number;
  };
}

interface CachedStageInfo {
  id: string;
  name: string;
  probability: number;
  orderIndex: number;
}

interface StageStore {
  stages: OpportunityStage[];
  loading: boolean;
  fetchStages: () => Promise<void>;
  addStage: (stage: OpportunityStage) => void;
  updateStage: (id: string, updates: Partial<OpportunityStage>) => void;
  deleteStage: (id: string) => void;
  /** 上次載入的階段數（骨架卡片數量用，重新整理後 stages 還沒回來時） */
  getCachedStageCount: () => number;
  /** 上次載入的階段（商機列表先顯示階段名稱用） */
  getCachedStages: () => CachedStageInfo[];
}

// 上次載入的階段存在 localStorage；登出會清空 localStorage，不同帳號不會互相看到
const STAGES_STORAGE_KEY = 'stages_default';
const STAGE_COUNT_STORAGE_KEY = 'stageCount_default';

const readStoredStages = (): CachedStageInfo[] => {
  try {
    return JSON.parse(localStorage.getItem(STAGES_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const readStoredStageCount = (): number => {
  try {
    return parseInt(localStorage.getItem(STAGE_COUNT_STORAGE_KEY) || '', 10) || 6; // 預設 6 張卡片
  } catch {
    return 6;
  }
};

const storeStages = (stages: CachedStageInfo[]) => {
  try {
    localStorage.setItem(STAGES_STORAGE_KEY, JSON.stringify(stages));
    localStorage.setItem(STAGE_COUNT_STORAGE_KEY, String(stages.length));
  } catch (error) {
    console.warn('Failed to cache stages:', error);
  }
};

let stagesRequest: Promise<void> | null = null;

export const useStageStore = create<StageStore>((set, get) => ({
  stages: [],
  loading: false,

  // stale-while-revalidate：已有階段時不顯示載入中、背景重抓（原本抓過一次就不再更新，別人改的階段要重新整理才看得到）
  fetchStages: async () => {
    if (stagesRequest) return stagesRequest;
    if (get().stages.length === 0) set({ loading: true });

    stagesRequest = (async () => {
      try {
        const stages = (await getStages()).data;
        set({ stages });
        storeStages(stages.map(({ id, name, probability, orderIndex }) => ({ id, name, probability, orderIndex })));
      } catch (error) {
        console.error('載入商機階段失敗:', error);
      } finally {
        set({ loading: false });
        stagesRequest = null;
      }
    })();
    return stagesRequest;
  },

  // 新增／編輯／刪除階段都在 API 成功後呼叫：階段影響銷售漏斗與階段統計，一併通知
  addStage: (stage: OpportunityStage) => {
    set(state => ({ stages: [...state.stages, stage].sort((a, b) => a.orderIndex - b.orderIndex) }));
    notifyDataChanged('opportunities');
  },

  updateStage: (id: string, updates: Partial<OpportunityStage>) => {
    set(state => ({
      stages: state.stages
        .map(stage => stage.id === id ? { ...stage, ...updates } : stage)
        .sort((a, b) => a.orderIndex - b.orderIndex)
    }));
    notifyDataChanged('opportunities');
  },

  deleteStage: (id: string) => {
    set(state => ({ stages: state.stages.filter(stage => stage.id !== id) }));
    notifyDataChanged('opportunities');
  },

  getCachedStageCount: readStoredStageCount,
  getCachedStages: readStoredStages,
}));
